import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiService } from '../../services/api'

interface TeamMemberRow {
  userId: string
  firstName: string
  lastName: string
  gender: string
  paragliderManufacturer: string
  paragliderModel: string
  faiNation: string
}

export default function TeamManagement() {
  const queryClient = useQueryClient()

  const [expandedTeamId, setExpandedTeamId] = useState<string | null>(null)
  const [renamingTeamId, setRenamingTeamId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [renameError, setRenameError] = useState<string | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 4000)
  }

  const { data: settingsData, isLoading: settingsLoading } = useQuery(
    'adminTeamSettings',
    () => apiService.getTeamSettings()
  )

  const { data: teamsData, isLoading: teamsLoading } = useQuery(
    'adminTeams',
    () => apiService.listTeams()
  )

  const { data: unassignedData, isLoading: unassignedLoading } = useQuery(
    'adminUnassignedPilots',
    () => apiService.listUnassignedPilots()
  )

  const { data: membersData, isLoading: membersLoading } = useQuery(
    ['adminTeamMembers', expandedTeamId],
    () => expandedTeamId ? apiService.listTeamMembers(expandedTeamId) : null,
    { enabled: !!expandedTeamId }
  )

  const renameMutation = useMutation(
    async ({ teamId, teamName }: { teamId: string; teamName: string }) => {
      const response = await apiService.renameTeam(teamId, teamName)
      if (response.error) throw new Error(response.error.message)
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminTeams')
        setRenamingTeamId(null)
        setRenameValue('')
        setRenameError(null)
        showToast('Team renamed successfully', 'success')
      },
      onError: (error: any) => {
        const message = error?.message || 'Failed to rename team'
        setRenameError(message)
      },
    }
  )

  const deleteMutation = useMutation(
    async (teamId: string) => {
      const response = await apiService.deleteTeam(teamId)
      if (response.error) throw new Error(response.error.message)
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminTeams')
        queryClient.invalidateQueries('adminUnassignedPilots')
        if (expandedTeamId === deleteConfirmId) {
          setExpandedTeamId(null)
        }
        setDeleteConfirmId(null)
        showToast('Team deleted successfully', 'success')
      },
      onError: (error: any) => {
        const message = error?.message || 'Failed to delete team'
        showToast(message, 'error')
        setDeleteConfirmId(null)
      },
    }
  )

  const assignPilotMutation = useMutation(
    async ({ teamId, userId }: { teamId: string; userId: string }) => {
      const response = await apiService.assignPilot(teamId, userId)
      if (response.error) throw new Error(response.error.message)
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminTeams')
        queryClient.invalidateQueries('adminUnassignedPilots')
        if (expandedTeamId) {
          queryClient.invalidateQueries(['adminTeamMembers', expandedTeamId])
        }
        showToast('Pilot assigned to team', 'success')
      },
      onError: (error: any) => {
        const message = error?.message || 'Failed to assign pilot'
        showToast(message, 'error')
      },
    }
  )

  const featureFlagMutation = useMutation(
    async (enabled: boolean) => {
      const compYear = settingsData?.data?.competitionYear || '2026'
      const response = await apiService.setTeamFeatureFlag(compYear, enabled)
      if (response.error) throw new Error(response.error.message)
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminTeamSettings')
        showToast('Team feature updated', 'success')
      },
      onError: (error: any) => {
        showToast(error?.message || 'Failed to update team feature', 'error')
      },
    }
  )

  const maxTeamSizeMutation = useMutation(
    async (maxTeamSize: number) => {
      const compYear = settingsData?.data?.competitionYear || '2026'
      const response = await apiService.setMaxTeamSize(compYear, maxTeamSize)
      if (response.error) throw new Error(response.error.message)
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminTeamSettings')
        showToast('Max team size updated', 'success')
      },
      onError: (error: any) => {
        showToast(error?.message || 'Failed to update max team size', 'error')
      },
    }
  )

  const removePilotMutation = useMutation(
    async (userId: string) => {
      const response = await apiService.removePilot(userId)
      if (response.error) throw new Error(response.error.message)
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminTeams')
        queryClient.invalidateQueries('adminUnassignedPilots')
        if (expandedTeamId) {
          queryClient.invalidateQueries(['adminTeamMembers', expandedTeamId])
        }
        showToast('Pilot removed from team', 'success')
      },
      onError: (error: any) => {
        const message = error?.message || 'Failed to remove pilot'
        showToast(message, 'error')
      },
    }
  )

  const isLoading = settingsLoading || teamsLoading || unassignedLoading

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  const settings = settingsData?.data
  const teams = teamsData?.data
  const unassigned = unassignedData?.data
  const members: TeamMemberRow[] = (membersData?.data as any)?.members ?? []

  const handleToggleExpand = (teamId: string) => {
    if (expandedTeamId === teamId) {
      setExpandedTeamId(null)
    } else {
      setExpandedTeamId(teamId)
    }
  }

  const handleStartRename = (teamId: string, currentName: string) => {
    setRenamingTeamId(teamId)
    setRenameValue(currentName)
    setRenameError(null)
  }

  const handleCancelRename = () => {
    setRenamingTeamId(null)
    setRenameValue('')
    setRenameError(null)
  }

  const handleSubmitRename = (teamId: string) => {
    const trimmed = renameValue.trim()
    if (!trimmed) {
      setRenameError('Team name cannot be empty')
      return
    }
    renameMutation.mutate({ teamId, teamName: trimmed })
  }

  const handleRenameKeyDown = (e: React.KeyboardEvent, teamId: string) => {
    if (e.key === 'Enter') {
      handleSubmitRename(teamId)
    } else if (e.key === 'Escape') {
      handleCancelRename()
    }
  }

  return (
    <div className="space-y-6">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          <div className="flex items-center gap-2">
            <span>{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2 text-white/80 hover:text-white">&times;</button>
          </div>
        </div>
      )}

      <h2 className="text-2xl font-bold text-gray-900">Team Management</h2>

      {/* Settings Panel */}
      <section className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Team Feature</p>
              <p className="text-xs text-gray-500">Enable or disable the team feature for pilots</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={settings?.teamFeatureEnabled ?? false}
              aria-label="Toggle team feature"
              disabled={featureFlagMutation.isLoading}
              onClick={() => featureFlagMutation.mutate(!settings?.teamFeatureEnabled)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                settings?.teamFeatureEnabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  settings?.teamFeatureEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Max Team Size</p>
              <p className="text-xs text-gray-500">Maximum number of pilots per team (3–8)</p>
            </div>
            <select
              value={settings?.maxTeamSize ?? 6}
              disabled={maxTeamSizeMutation.isLoading}
              onChange={(e) => maxTeamSizeMutation.mutate(Number(e.target.value))}
              className="rounded-md border-gray-300 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50"
              aria-label="Max team size"
            >
              {[3, 4, 5, 6, 7, 8].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Team List */}
      <section className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Teams</h3>
        {teams?.teams && teams.teams.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {teams.teams.map((team) => {
              const isExpanded = expandedTeamId === team.teamId
              const isRenaming = renamingTeamId === team.teamId
              const isDeleting = deleteConfirmId === team.teamId

              return (
                <li key={team.teamId} className="py-1">
                  {/* Team row header */}
                  <div
                    className={`flex items-center justify-between py-3 px-3 rounded cursor-pointer transition-colors ${
                      team.isFull ? 'bg-red-100 text-red-700' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => handleToggleExpand(team.teamId)}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {/* Expand/collapse chevron */}
                      <svg
                        className={`w-4 h-4 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>

                      {/* Team name or rename input */}
                      {isRenaming ? (
                        <div className="flex items-center gap-2 flex-1" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="text"
                            value={renameValue}
                            onChange={(e) => { setRenameValue(e.target.value); setRenameError(null) }}
                            onKeyDown={(e) => handleRenameKeyDown(e, team.teamId)}
                            className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent flex-1"
                            autoFocus
                          />
                          <button
                            onClick={() => handleSubmitRename(team.teamId)}
                            disabled={renameMutation.isLoading}
                            className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            Save
                          </button>
                          <button
                            onClick={handleCancelRename}
                            className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <span className="text-sm font-medium truncate">{team.teamName}</span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      {/* Member count badge */}
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        team.isFull ? 'bg-red-200 text-red-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {team.memberCount}/{teams.maxTeamSize}
                      </span>

                      {/* Rename button */}
                      {!isRenaming && (
                        <button
                          onClick={() => handleStartRename(team.teamId, team.teamName)}
                          className="text-xs px-2 py-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                          title="Rename team"
                        >
                          Rename
                        </button>
                      )}

                      {/* Delete button / confirmation dialog */}
                      {isDeleting ? (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-red-600 font-medium">Delete?</span>
                          <button
                            onClick={() => deleteMutation.mutate(team.teamId)}
                            disabled={deleteMutation.isLoading}
                            className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(null)}
                            className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirmId(team.teamId)}
                          className="text-xs px-2 py-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                          title="Delete team"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Rename error */}
                  {isRenaming && renameError && (
                    <p className="text-xs text-red-600 mt-1 ml-10">{renameError}</p>
                  )}

                  {/* Expanded member table */}
                  {isExpanded && (
                    <div className="ml-7 mt-2 mb-3 border border-gray-200 rounded-lg overflow-hidden">
                      {membersLoading ? (
                        <div className="flex justify-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        </div>
                      ) : members.length === 0 ? (
                        <p className="text-sm text-gray-500 py-4 px-4">No pilots assigned to this team</p>
                      ) : (
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Gender</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Glider</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">FAI Nation</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {members.map((member) => (
                              <tr key={member.userId} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  {member.firstName} {member.lastName}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-600">
                                  {member.gender}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-600">
                                  {member.paragliderManufacturer} {member.paragliderModel}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-600">
                                  {member.faiNation}
                                </td>
                                <td className="px-4 py-2 text-right">
                                  <button
                                    onClick={() => removePilotMutation.mutate(member.userId)}
                                    disabled={removePilotMutation.isLoading}
                                    className="text-xs px-2 py-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded disabled:opacity-50"
                                  >
                                    Remove from team
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="text-sm text-gray-500">No teams created yet.</p>
        )}
      </section>

      {/* Unassigned Pilots Panel */}
      <section className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Unassigned Pilots</h3>
        {unassigned?.pilots && unassigned.pilots.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {unassigned.pilots.map((pilot) => (
              <li key={pilot.userId} className="flex items-center justify-between py-3 px-2">
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {pilot.firstName} {pilot.lastName}
                  </span>
                  <span className="ml-2 text-xs text-gray-500">{pilot.faiNation}</span>
                </div>
                <select
                  className="text-xs border border-gray-300 rounded px-2 py-1"
                  defaultValue=""
                  onChange={(e) => {
                    const teamId = e.target.value
                    if (teamId) {
                      assignPilotMutation.mutate({ teamId, userId: pilot.userId })
                      e.target.value = ''
                    }
                  }}
                  aria-label={`Assign ${pilot.firstName} ${pilot.lastName}`}
                >
                  <option value="" disabled>Assign to team…</option>
                  {teams?.teams?.map((team) => (
                    <option key={team.teamId} value={team.teamId} disabled={team.isFull}>
                      {team.teamName}{team.isFull ? ' (full)' : ''}
                    </option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500">All pilots are assigned to teams.</p>
        )}
      </section>
    </div>
  )
}
